import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Rss, ExternalLink, Newspaper, Heart, Stethoscope, BookOpen, ShieldCheck, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_MAP = {
  industry_news: { label: 'News', icon: Newspaper, color: 'bg-blue-100 text-blue-700' },
  health_training: { label: 'Health & Training', icon: Stethoscope, color: 'bg-green-100 text-green-700' },
  health_news: { label: 'Health', icon: Stethoscope, color: 'bg-green-100 text-green-700' },
  rescue_news: { label: 'Rescue', icon: Heart, color: 'bg-pink-100 text-pink-700' },
  breed_news: { label: 'Breeds', icon: BookOpen, color: 'bg-amber-100 text-amber-700' },
  general: { label: 'General', icon: Rss, color: 'bg-gray-100 text-gray-700' },
};

function FeedCard({ feed }) {
  const cat = CATEGORY_MAP[feed.category] || CATEGORY_MAP.general;
  const CatIcon = cat.icon;

  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`feed-card-${feed.name?.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              {feed.is_trusted && (
                <ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
              )}
              <h3 className="font-semibold text-sm truncate">{feed.name}</h3>
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
              {feed.description}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary" className={`text-[10px] ${cat.color}`}>
                <CatIcon className="w-3 h-3 mr-1" />
                {cat.label}
              </Badge>
              {feed.article_count > 0 && (
                <span className="text-[10px] text-muted-foreground">
                  {feed.article_count} articles
                </span>
              )}
              {feed.is_featured && (
                <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">
                  Featured
                </Badge>
              )}
            </div>
          </div>
          <a
            href={feed.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0"
          >
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <ExternalLink className="w-4 h-4" />
            </Button>
          </a>
        </div>
      </CardContent>
    </Card>
  );
}

export default function FeedDirectory() {
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeeds = async () => {
      try {
        const response = await fetch(`${API}/api/content-sources/feeds/directory`);
        if (response.ok) {
          const data = await response.json();
          setFeeds(data.feeds || []);
        }
      } catch (error) {
        console.error('Error fetching feed directory:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeeds();
  }, []);

  const totalArticles = feeds.reduce((sum, f) => sum + (f.article_count || 0), 0);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="feed-directory-page">
      <div className="mb-8">
        <Link to="/blog" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="w-3 h-3" /> Back to articles
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
            <Rss className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">News Feeds</h1>
            <p className="text-sm text-muted-foreground">
              {feeds.length} sources, {totalArticles} articles
            </p>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          We curate dog-related content from these trusted sources. Articles appear in our
          {' '}<Link to="/blog" className="text-primary underline">blog</Link> with full attribution and links to the original.
          More sources and user preferences coming soon.
        </p>
      </div>

      {loading ? (
        <div className="grid gap-3 md:grid-cols-2">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-5">
                <div className="h-4 bg-muted rounded w-2/3 mb-2" />
                <div className="h-3 bg-muted rounded w-full mb-1" />
                <div className="h-3 bg-muted rounded w-4/5" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : feeds.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <Rss className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No feeds configured yet.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Active feeds */}
          <div className="grid gap-3 md:grid-cols-2">
            {feeds.map((feed, idx) => (
              <FeedCard key={feed.feed_url || idx} feed={feed} />
            ))}
          </div>

          {/* Coming soon teaser */}
          <Card className="mt-8 border-dashed">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                More feeds coming soon
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-xs text-muted-foreground">
                We're evaluating additional sources including Kinship, Dogtopia, PetMD, and others from the 
                Feedspot Top 100 Dog RSS Feeds directory. Eventually you'll be able to choose which feeds appear 
                in your personal stream, filter by category, and control how often they update.
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
